import {
  type CrossResourceAvailabilityRuleId,
  dependentRequirementRank,
  isSupplierAvailableFromGrants,
  principalsWithPermission,
  supplierRoleForGrant,
} from "../../shared/rbac/crossResourceAvailability";
import type { GrantRow } from "../../shared/rbac/grantQueries";
import type { Permission } from "../../shared/rbac/permissions";
import type { GrantEntry } from "../../shared/rbac/roles";
import { principalsCrossingIntoRequirement } from "../../shared/rbac/validation/crossResource";

// Supplier grants carry the raw customRoleId (needed to preserve custom-role
// grants when we rewrite the supplier's full grant list) alongside the
// customRole.rolePermissions the availability check reads.
export type SupplierGrantRow = GrantRow & { customRoleId?: string | null };

export type SupplierInput = {
  id: string;
  name: string;
  // Connector template identifier, for the brand logo (skills have none).
  identifier?: string;
  roleGrantsAsScope: readonly SupplierGrantRow[];
};

export type SupplierGap = {
  ruleId: CrossResourceAvailabilityRuleId;
  scopeKind: "connected_app" | "skill";
  supplierId: string;
  supplierName: string;
  identifier?: string;
  // Whether the acting admin can grant access here themselves.
  canShare: boolean;
  // The minimal grants to add for the crossing members that lack this supplier.
  additions: GrantEntry[];
  // The supplier's current grants, preserved when rewriting its full grant list.
  existingGrants: GrantEntry[];
  // Who to ask when the admin can't share it themselves.
  sharerUserIds: string[];
  sharerGroupIds: string[];
};

function principalKey(g: {
  userGroupId?: string | null;
  userId?: string | null;
  serviceAccountId?: string | null;
}): string | null {
  return g.userGroupId
    ? `g:${g.userGroupId}`
    : g.userId
      ? `u:${g.userId}`
      : g.serviceAccountId
        ? `s:${g.serviceAccountId}`
        : null;
}

function toGrantEntry(g: SupplierGrantRow): GrantEntry {
  return {
    userGroupId: g.userGroupId ?? undefined,
    userId: g.userId ?? undefined,
    serviceAccountId: g.serviceAccountId ?? undefined,
    builtinRoleId: g.builtinRoleId ?? undefined,
    customRoleId: g.customRoleId ?? undefined,
  };
}

// One minimal grant per lacking principal, at its strongest space tier — the
// least-privilege supplier role the mutation guard requires (editor →
// connected_app_user, viewer → connected_app_viewer, any space role → skill_user).
function additionsFor(
  ruleId: CrossResourceAvailabilityRuleId,
  lacking: readonly GrantRow[]
): GrantEntry[] {
  const strongest = new Map<string, { grant: GrantRow; rank: number }>();
  for (const g of lacking) {
    const key = principalKey(g);
    if (!key) continue;
    const rank = dependentRequirementRank(ruleId, g);
    const prev = strongest.get(key);
    if (!prev || rank < prev.rank) strongest.set(key, { grant: g, rank });
  }
  const additions: GrantEntry[] = [];
  for (const { grant } of strongest.values()) {
    const builtinRoleId = supplierRoleForGrant(ruleId, grant);
    if (!builtinRoleId) continue;
    additions.push({
      userGroupId: grant.userGroupId ?? undefined,
      userId: grant.userId ?? undefined,
      serviceAccountId: grant.serviceAccountId ?? undefined,
      builtinRoleId,
    });
  }
  return additions;
}

type RuleSpec = {
  ruleId: CrossResourceAvailabilityRuleId;
  scopeKind: "connected_app" | "skill";
  suppliers: readonly SupplierInput[];
  manageableIds: ReadonlySet<string>;
  sharePermission: Permission;
};

// The connectors and skills a space uses that a newly-added (or upgraded) member
// would lack. Callers MUST pass the space's *used* suppliers (mirroring the
// server's spaceAppEdges/spaceSkillEdges via usedSuppliersForSpace) — not the
// usable/display intersection — or the "matches what the server enforces"
// guarantee breaks and the modal is skipped for a supplier the guard rejects.
// Reuses the exact primitives the mutation guard runs
// (principalsCrossingIntoRequirement + isSupplierAvailableFromGrants).
export function computeSpaceMemberSupplierGap(input: {
  currentSpaceGrants: readonly GrantRow[];
  desiredSpaceGrants: readonly GrantRow[];
  connectors: readonly SupplierInput[];
  skills: readonly SupplierInput[];
  memberships: ReadonlyMap<string, readonly string[]>;
  manageableConnectorIds: ReadonlySet<string>;
  manageableSkillIds: ReadonlySet<string>;
}): SupplierGap[] {
  const specs: RuleSpec[] = [
    {
      ruleId: "space_uses_connected_apps",
      scopeKind: "connected_app",
      suppliers: input.connectors,
      manageableIds: input.manageableConnectorIds,
      sharePermission: "connected_app:share",
    },
    {
      ruleId: "space_uses_skills",
      scopeKind: "skill",
      suppliers: input.skills,
      manageableIds: input.manageableSkillIds,
      sharePermission: "skill:write",
    },
  ];

  const gaps: SupplierGap[] = [];
  for (const spec of specs) {
    const crossing = principalsCrossingIntoRequirement(
      spec.ruleId,
      input.currentSpaceGrants,
      input.desiredSpaceGrants
    );
    if (crossing.length === 0) continue;
    for (const supplier of spec.suppliers) {
      const lacking = crossing.filter(
        (g) =>
          !isSupplierAvailableFromGrants(
            spec.ruleId,
            [g],
            supplier.roleGrantsAsScope,
            input.memberships
          )
      );
      if (lacking.length === 0) continue;
      const additions = additionsFor(spec.ruleId, lacking);
      if (additions.length === 0) continue;
      const sharers = principalsWithPermission(
        supplier.roleGrantsAsScope,
        spec.sharePermission
      );
      gaps.push({
        ruleId: spec.ruleId,
        scopeKind: spec.scopeKind,
        supplierId: supplier.id,
        supplierName: supplier.name,
        identifier: supplier.identifier,
        canShare: spec.manageableIds.has(supplier.id),
        additions,
        existingGrants: supplier.roleGrantsAsScope
          .filter((g) => g.userGroupId || g.userId || g.serviceAccountId)
          .map(toGrantEntry),
        sharerUserIds: [...sharers.userIds],
        sharerGroupIds: [...sharers.groupIds],
      });
    }
  }
  return gaps;
}
